import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { API_BASE_URL, getToken } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { useProtocolShell } from "@/contexts/ProtocolShellContext";
import { formatSvDateTime } from "@/utils/date";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function buildRealtimeUrl() {
  if (!API_BASE_URL) return "";
  if (API_BASE_URL.startsWith("https://")) {
    return `${API_BASE_URL.replace(/^https:\/\//, "wss://").replace(/\/$/, "")}/api/realtime/ws`;
  }
  if (API_BASE_URL.startsWith("http://")) {
    return `${API_BASE_URL.replace(/^http:\/\//, "ws://").replace(/\/$/, "")}/api/realtime/ws`;
  }
  return "";
}

function describeMatch(event) {
  const title = [event?.home_team, event?.away_team].filter(Boolean).join(" vs ");
  const when = event?.match_start_formatted || (event?.match_start
    ? formatSvDateTime(event.match_start, {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null);
  return {
    title: title || "Aktuell match",
    when,
  };
}

export default function LineupRealtimeBridge() {
  const queryClient = useQueryClient();
  const { user, ready } = useAuth();
  const {
    activeProtocolMatchId,
    isOpen,
    focusProtocolLineupAlert,
    setIsOpen,
  } = useProtocolShell();

  const [activeAlert, setActiveAlert] = React.useState(null);
  const socketRef = React.useRef(null);
  const activeProtocolMatchIdRef = React.useRef(activeProtocolMatchId);
  const reconnectTimerRef = React.useRef(null);
  const pingTimerRef = React.useRef(null);
  const shouldReconnectRef = React.useRef(true);
  const intentionalCloseRef = React.useRef(false);
  const retryCountRef = React.useRef(0);

  React.useEffect(() => {
    activeProtocolMatchIdRef.current = activeProtocolMatchId;
  }, [activeProtocolMatchId]);

  const cleanupSocket = React.useCallback(() => {
    if (pingTimerRef.current) {
      window.clearInterval(pingTimerRef.current);
      pingTimerRef.current = null;
    }
    if (socketRef.current) {
      intentionalCloseRef.current = true;
      try {
        socketRef.current.close();
      } catch {
        // ignore
      }
      socketRef.current = null;
    }
  }, []);

  const scheduleReconnect = React.useCallback(() => {
    if (!shouldReconnectRef.current || reconnectTimerRef.current) return;
    const delay = Math.min(1000 * 2 ** retryCountRef.current, 15000);
    reconnectTimerRef.current = window.setTimeout(() => {
      reconnectTimerRef.current = null;
      retryCountRef.current += 1;
      connect();
    }, delay);
  }, []);

  const handleLineupChanged = React.useCallback((event) => {
    const matchId = String(event?.match_id || "");
    const officialMatchId = String(event?.official_match_id || "");

    if (matchId) {
      queryClient.invalidateQueries({ queryKey: ["match", matchId] });
    }
    if (officialMatchId) {
      queryClient.invalidateQueries({ queryKey: ["officialMatch", officialMatchId] });
    }
    queryClient.invalidateQueries({ queryKey: ["matches"] });
    queryClient.invalidateQueries({ queryKey: ["matchesSummary"] });
    queryClient.invalidateQueries({ queryKey: ["userMatches"] });
    queryClient.invalidateQueries({ queryKey: ["userMatchesSummary"] });
    queryClient.invalidateQueries({ queryKey: ["notifications", "unreadCount"] });
    queryClient.invalidateQueries({ queryKey: ["notifications", "unseenCount"] });
    queryClient.invalidateQueries({ queryKey: ["notifications", "inbox"] });

    const hasActiveSameMatch =
      activeProtocolMatchIdRef.current && String(activeProtocolMatchIdRef.current) === matchId;

    if (hasActiveSameMatch) {
      setActiveAlert({
        matchId,
        officialMatchId,
        officialHasLineup: Boolean(event?.official_has_lineup),
        matchTitle: describeMatch(event).title,
        matchWhen: describeMatch(event).when,
      });
      return;
    }

    const matchMeta = describeMatch(event);
    toast("Laguppställningen har ändrats", {
      description: `${matchMeta.title}${matchMeta.when ? ` • ${matchMeta.when}` : ""}`,
    });
  }, [queryClient]);

  const handleLineupPublished = React.useCallback((event) => {
    const officialMatchId = String(event?.official_match_id || "");
    if (officialMatchId) {
      queryClient.invalidateQueries({ queryKey: ["officialMatch", officialMatchId] });
    }
    queryClient.invalidateQueries({ queryKey: ["officialMatches"] });
    queryClient.invalidateQueries({ queryKey: ["notifications", "unreadCount"] });
    queryClient.invalidateQueries({ queryKey: ["notifications", "unseenCount"] });
    queryClient.invalidateQueries({ queryKey: ["notifications", "inbox"] });

    const matchMeta = describeMatch(event);
    toast("Laguppställningen är publicerad", {
      description: `${matchMeta.title}${matchMeta.when ? ` • ${matchMeta.when}` : ""}`,
    });
  }, [queryClient]);

  const handleLineupRebased = React.useCallback((event) => {
    const matchId = String(event?.match_id || "");
    const officialMatchId = String(event?.official_match_id || "");

    if (matchId) {
      queryClient.invalidateQueries({ queryKey: ["match", matchId] });
    }
    if (officialMatchId) {
      queryClient.invalidateQueries({ queryKey: ["officialMatch", officialMatchId] });
    }
    queryClient.invalidateQueries({ queryKey: ["matches"] });
    queryClient.invalidateQueries({ queryKey: ["matchesSummary"] });
    queryClient.invalidateQueries({ queryKey: ["userMatches"] });
    queryClient.invalidateQueries({ queryKey: ["userMatchesSummary"] });
  }, [queryClient]);

  const connect = React.useCallback(() => {
    const token = getToken();
    const url = buildRealtimeUrl();
    if (!ready || !user?.id || !token || !url) return;

    cleanupSocket();
    intentionalCloseRef.current = false;

    try {
      const socket = new window.WebSocket(url);
      socketRef.current = socket;

      socket.addEventListener("open", () => {
        retryCountRef.current = 0;
        socket.send(JSON.stringify({ type: "auth", token }));
        queryClient.invalidateQueries({ queryKey: ["match"] });
        queryClient.invalidateQueries({ queryKey: ["matches"] });
        queryClient.invalidateQueries({ queryKey: ["matchesSummary"] });
        queryClient.invalidateQueries({ queryKey: ["userMatches"] });
        queryClient.invalidateQueries({ queryKey: ["userMatchesSummary"] });
        pingTimerRef.current = window.setInterval(() => {
          if (socket.readyState === window.WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: "ping" }));
          }
        }, 30000);
      });

      socket.addEventListener("message", (message) => {
        try {
          const payload = JSON.parse(message.data);
          if (payload?.type === "lineup_changed") {
            handleLineupChanged(payload);
          } else if (payload?.type === "lineup_published") {
            handleLineupPublished(payload);
          } else if (payload?.type === "lineup_rebased") {
            handleLineupRebased(payload);
          }
        } catch {
          // ignore malformed payloads
        }
      });

      socket.addEventListener("close", () => {
        if (socketRef.current === socket) {
          socketRef.current = null;
        }
        if (pingTimerRef.current) {
          window.clearInterval(pingTimerRef.current);
          pingTimerRef.current = null;
        }
        if (intentionalCloseRef.current) {
          intentionalCloseRef.current = false;
          return;
        }
        scheduleReconnect();
      });

      socket.addEventListener("error", () => {
        if (socketRef.current === socket) {
          socketRef.current = null;
        }
        if (pingTimerRef.current) {
          window.clearInterval(pingTimerRef.current);
          pingTimerRef.current = null;
        }
      });
    } catch {
      scheduleReconnect();
    }
  }, [cleanupSocket, handleLineupChanged, handleLineupPublished, handleLineupRebased, ready, scheduleReconnect, user?.id]);

  React.useEffect(() => {
    shouldReconnectRef.current = true;
    const timer = window.setTimeout(() => {
      connect();
    }, 0);

    return () => {
      shouldReconnectRef.current = false;
      window.clearTimeout(timer);
      if (reconnectTimerRef.current) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      cleanupSocket();
    };
  }, [cleanupSocket, connect]);

  React.useEffect(() => {
    if (activeAlert?.matchId && String(activeProtocolMatchId || "") !== String(activeAlert.matchId)) {
      setActiveAlert(null);
    }
  }, [activeAlert?.matchId, activeProtocolMatchId]);

  const acknowledgeActiveAlert = React.useCallback(() => {
    if (activeAlert?.matchId) {
      focusProtocolLineupAlert(activeAlert.matchId);
    }
    setActiveAlert(null);
  }, [activeAlert?.matchId, focusProtocolLineupAlert]);

  return (
    <AlertDialog open={Boolean(activeAlert)} onOpenChange={(open) => !open && setActiveAlert(null)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Laguppställningen har ändrats</AlertDialogTitle>
          <AlertDialogDescription>
            <span className="mb-2 block font-medium text-foreground">
              {activeAlert?.matchTitle}
              {activeAlert?.matchWhen ? ` • ${activeAlert.matchWhen}` : ""}
            </span>
            {activeAlert?.officialHasLineup
              ? "Den officiella laguppställningen för ditt pågående protokoll har ändrats. Appen har hämtat den nya statusen så att du kan ta ställning direkt."
              : "Den officiella laguppställningen för ditt pågående protokoll har tagits bort. Appen har hämtat den nya statusen så att du kan ta ställning direkt."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          {!isOpen ? (
            <AlertDialogAction
              onClick={() => {
                setIsOpen(true);
                acknowledgeActiveAlert();
              }}
            >
              Öppna protokollet
            </AlertDialogAction>
          ) : (
            <AlertDialogAction onClick={acknowledgeActiveAlert}>
              Förstått
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
